import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, FileText, Loader2 } from "lucide-react";

const TEMPLATES = [
  {
    id: "privacy",
    title: "Политика конфиденциальности",
    build: (s) => `ПОЛИТИКА КОНФИДЕНЦИАЛЬНОСТИ

Оператор: ${s.legal_name || s.company_name || "—"}
Адрес: ${s.address || "—"}
Контактный email: ${s.email || "—"}
Лицензия: ${s.license_number ? `№ ${s.license_number} · ${s.license_activity || "Commercial Brokers"}` : "—"}

1. ОБЩИЕ ПОЛОЖЕНИЯ
Настоящая Политика конфиденциальности описывает, как ${s.company_name || "Оператор"} (далее — «Оператор») обрабатывает персональные данные посетителей сайта, оставивших запрос через форму обратной связи.

2. КАКИЕ ДАННЫЕ МЫ СОБИРАЕМ
Имя, название компании, адрес электронной почты, контакт в WhatsApp или Telegram, параметры запроса (направление, объём, текст сообщения), а также технические данные о посещении сайта (IP-адрес, источник перехода, страна).

3. ЦЕЛИ ОБРАБОТКИ
Обработка запросов и связь с заявителем; проведение процедур KYC/AML; улучшение работы сайта и анализ посещаемости.

4. ПЕРЕДАЧА ДАННЫХ
Оператор не продаёт и не передаёт персональные данные третьим лицам, за исключением случаев, предусмотренных законодательством, а также верифицированным контрагентам в рамках согласованной сделки с согласия заявителя.

5. ХРАНЕНИЕ И ЗАЩИТА
Данные хранятся на защищённых серверах. Доступ ограничен уполномоченными сотрудниками. Все обращения обрабатываются под NDA.

6. ПРАВА СУБЪЕКТА ДАННЫХ
Вы вправе запросить уточнение, блокирование или удаление своих данных, направив запрос на ${s.email || "контактный email"}.

Дата публикации: ${new Date().toLocaleDateString("ru-RU")}`,
  },
  {
    id: "pdn",
    title: "Согласие на обработку персональных данных",
    build: (s) => `СОГЛАСИЕ НА ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ

Оставляя запрос на сайте ${s.company_name || "—"}, я свободно, своей волей и в своём интересе даю согласие ${s.legal_name || s.company_name || "Оператору"} (${s.address || "—"}${s.license_number ? `, лицензия № ${s.license_number}` : ""}) на обработку моих персональных данных: имени, названия компании, адреса электронной почты, контакта в WhatsApp / Telegram и содержания запроса.

Цели обработки: обработка запроса, связь со мной, проведение процедур идентификации (KYC/AML), подготовка коммерческих условий.

Согласие действует с момента отправки формы до момента отзыва. Отозвать согласие можно письмом на ${s.email || "контактный email"}.

Я уведомлён(а), что ${s.company_name || "Оператор"} действует исключительно как интродьюсер и не является продавцом или покупателем товара.`,
  },
  {
    id: "disclaimer",
    title: "Дисклеймер интродьюсера",
    build: (s) => `ДИСКЛЕЙМЕР

${s.company_name || "Компания"} (${s.legal_name || "—"}, ${s.address || "—"}${s.license_number ? `, лицензия № ${s.license_number}` : ""}) действует как лицензированный коммерческий брокер и посредник между покупателями и ресурсодержателями.

${s.company_name || "Компания"}:
— не является продавцом, покупателем или бенефициаром по контрактам поставки золота или нефтепродуктов;
— не владеет товаром и не осуществляет его поставку;
— получает вознаграждение в виде комиссии от 1% от суммы контракта. Комиссия не является единоразовой: она выплачивается ежемесячно, в течение всего срока действия контракта, по мере поступления оплат за каждую поставку;
— не гарантирует коммерческие условия, которые определяются исключительно контрактом (SPA) между верифицированными сторонами.

Вся информация на сайте носит предварительный характер и не является публичной офертой. Итоговые условия определяются SPA, KYC/AML-проверкой и согласованной банковской процедурой.

Контакт для вопросов: ${s.email || "—"}`,
  },
];

export const Documents = ({ api, demo }) => {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (demo) {
      setSettings({
        company_name: "MELNIK Commercial Brokers",
        legal_name: "MELNIK Commercial Brokers",
        address: "Dubai, UAE",
        email: "info@melnikbrokers.ae",
        license_number: "1656588",
        license_activity: "Commercial Brokers",
      });
      return;
    }
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Скопировано в буфер обмена"));
  };

  const download = (doc, text) => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${doc.id}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="admin-documents" className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-serif text-2xl text-slate-50 sm:text-3xl">Документы</h1>
        <p className="mt-2 max-w-xl text-sm text-slate-500">
          Шаблоны генерируются автоматически из данных раздела «Настройки» (юр. название, лицензия, адрес, email). Перед публикацией
          покажите текст юристу.
        </p>
      </div>

      <div className="space-y-4">
        {TEMPLATES.map((doc) => {
          const text = doc.build(settings);
          const isOpen = open === doc.id;
          return (
            <div key={doc.id} data-testid={`doc-card-${doc.id}`} className="rounded-[1.5rem] border border-white/8 bg-[#12141C] p-4 sm:rounded-[2rem] sm:p-7">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/8">
                    <FileText className="h-5 w-5 text-[#D4AF37]" />
                  </span>
                  <span className="text-base font-semibold text-slate-100">{doc.title}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOpen(isOpen ? null : doc.id)}
                    data-testid={`doc-generate-${doc.id}`}
                    className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-2.5 text-xs font-semibold text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/20"
                  >
                    {isOpen ? "Скрыть" : "Сгенерировать"}
                  </button>
                  <button
                    onClick={() => copy(text)}
                    data-testid={`doc-copy-${doc.id}`}
                    className="rounded-xl border border-white/10 px-4 py-2.5 text-xs text-slate-300 transition-colors hover:border-white/25"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => download(doc, text)}
                    data-testid={`doc-download-${doc.id}`}
                    className="rounded-xl border border-white/10 px-4 py-2.5 text-xs text-slate-300 transition-colors hover:border-white/25"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {isOpen && (
                <pre
                  data-testid={`doc-preview-${doc.id}`}
                  className="mt-5 max-h-96 overflow-auto whitespace-pre-wrap rounded-2xl border border-white/8 bg-white/[0.02] p-5 text-xs leading-relaxed text-slate-400"
                >
                  {text}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
